import { filterClasses } from "@swivl-mono/helpers";
import {
  ConversationMessage,
  type DebugLevel,
  LinkClick,
  agentFacingEscalationLabel,
  debugLevelAtLeast,
  messageMinDebugLevel
} from "@swivl-mono/types";
import { useEffect, useMemo, useRef } from "react";
import {
  IoAlertCircleOutline,
  IoArchiveOutline,
  IoCallOutline,
  IoChatbubbleEllipsesOutline,
  IoCheckmarkCircleOutline,
  IoCheckmarkDoneOutline,
  IoCloseCircleOutline,
  IoFlaskOutline,
  IoGitBranchOutline,
  IoHandRightOutline,
  IoLinkOutline,
  IoNavigateOutline,
  IoPeopleOutline,
  IoRefreshOutline,
  IoRemoveCircleOutline,
  IoRepeatOutline,
  IoRocketOutline,
  IoSearchOutline,
  IoSendOutline,
  IoSparklesOutline,
  IoStatsChartOutline,
  IoSwapHorizontalOutline,
  IoTerminalOutline,
  IoTicketOutline,
  IoTimerOutline,
  IoWarningOutline
} from "react-icons/io5";

import { formatTime } from "../Helpers/formatTime";
import type { ChatMessage } from "../Types/BotChat.Types";
import { HumanizedData } from "./HumanizedData";
import { InvisibleRow } from "./InvisibleRow";
import { LLMCallCard } from "./LLMCallCard";
import { MessageBubble } from "./MessageBubble";
import { StructuredMessage } from "./Structured/StructuredMessage";
import { SystemPromptCard } from "./SystemPromptCard";
import { TakeoverEventNotice, agentRefsFromMetadata, isTakeoverEventMessage, linkifyAgentNames } from "./TakeoverEventNotice";
import { ToolCallCard } from "./ToolCallCard";
import { TypingIndicator } from "./TypingIndicator";
import { anchorAnalyticsEvents } from "./anchorAnalyticsEvents";

/** Format elapsed time from conversation start for voice transcripts */
function formatElapsed(startTime: Date | string | undefined, msgTime: Date | string | undefined): string {
  if (!startTime || !msgTime) return "";
  const start = new Date(startTime).getTime();
  const msg = new Date(msgTime).getTime();
  const diffSec = Math.max(0, Math.round((msg - start) / 1000));
  const min = Math.floor(diffSec / 60);
  const sec = diffSec % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

/**
 * Voice footer: the wall-clock time the line actually landed, with how far
 * into the call it was in brackets.
 *
 * Elapsed used to be the whole label, which reads as a time without being one
 * — reviewing a closed call there was no way to tell what time of day, or what
 * date, anything was said, and the only absolute reference was a single date
 * divider at the top of the call (PROD-774). Elapsed stays because it is what
 * an agent follows during a live call; it just can no longer stand in for the
 * real time.
 *
 * Either half is dropped rather than rendered empty: a message with no
 * `createdAt` still shows its elapsed value, and a call whose start is unknown
 * still shows the clock.
 */
function formatVoiceTime(startTime: Date | string | undefined, msgTime: string | undefined): string {
  const clock = msgTime ? formatTime(msgTime) : "";
  const elapsed = formatElapsed(startTime, msgTime);
  if (!clock) return elapsed;
  return elapsed ? `${clock} (${elapsed})` : clock;
}

interface ConversationTimelineProps {
  /**
   * Messages in the canonical {@link ConversationMessage} render shape — the one
   * shape every chat surface (widget + agent console + debug timeline) consumes.
   * Callers map their source rows (CC `CCMessageItem`, AI-Conversations
   * `BotMessageInterface` entity rows) to this at the boundary. The agent-only
   * debug fields (`type`, `visible`, `metadata`, `botLLMCallId`) live on
   * `ConversationMessage` precisely so this timeline can render them.
   */
  messages: ConversationMessage[];
  /** Rendered at the top of the scroll area, above the messages — the agent
   *  console mounts its previous-conversations history stack here. */
  header?: React.ReactNode;
  /** Whether to show the typing indicator */
  isTyping?: boolean;
  /** Empty state text */
  emptyMessage?: string;
  /** Whether to show debug-level messages (system prompts, LLM calls). Default: false */
  showDebug?: boolean;
  /**
   * Debug visibility level forwarded to per-kind structured-message renderers
   * (e.g. Buttons.tsx surfaces `llmMessage` at `agent`+). Defaults to `"none"`.
   * Independent of `showDebug` (which gates `system_prompt` / `llm_call` cards).
   */
  debugLevel?: DebugLevel;
  /** Callback to fetch the full LLM call record when an LLM card is expanded */
  onRequestLLMRecord?: (botLLMCallId: number) => void;
  /** Map of LLM call ID → full record, for LLM call cards */
  llmRecords?: Map<number, any>;
  /** Channel — when "voice", renders messages as a transcript instead of chat bubbles */
  channel?: "web" | "sms" | "voice";
  /**
   * Kept for callers, unused since voice moved to the shared bubble layout —
   * the customer's own bubbles carry a time, not a name.
   */
  userName?: string;
  /** Agent name for voice transcript speaker labels */
  agentName?: string;
  /** Bubble palette forwarded to `MessageBubble` — the CC agent console passes "console" for v2 colours. */
  palette?: "widget" | "console";
  /**
   * Which role is right-aligned as "mine" — forwarded to `MessageBubble`.
   * Defaults to "user" so every existing surface keeps its current layout;
   * the CC agent console passes "assistant" so the agent (not the customer)
   * sits on the right. See `MessageBubble.ownRole`.
   */
  ownRole?: "user" | "assistant";
  /**
   * Read watermark for the "New Messages" divider — rendered before the first
   * plain message whose `messageIndex` exceeds it. Omit (or pass undefined)
   * to render no divider. -1 means "never read" (everything is new).
   */
  newMessagesAfterIndex?: number;
  /** When set, agent names in event rows deep-link to the agent's profile (ENG-2946). */
  onAgentClick?: (memberId: number) => void;
  /**
   * When set, event rows that carry a `ticketNumber` (ticket_created /
   * ticket_updated, ENG-3005) render a clickable "#N →" chip that opens the
   * ticket. The admin AI-conversations view omits it and renders plain text.
   */
  onTicketClick?: (ticketNumber: number) => void;
}

/**
 * The per-turn speed readout under a bot bubble: time to first *audio* on a
 * call, time to first *token* on text. Undefined when the turn carried no
 * timing, which is most of them.
 */
function latencyReadout(
  isVoice: boolean,
  meta: { ttfaMs?: number; turnFirstTokenLatencyMs?: number; turnLatencyMs?: number } | undefined
): string | undefined {
  const first = isVoice ? meta?.ttfaMs : meta?.turnFirstTokenLatencyMs;
  if (first === undefined) return undefined;
  const total = meta?.turnLatencyMs !== undefined ? ` · ${meta.turnLatencyMs}ms total` : "";
  return `${isVoice ? "TTFA" : "TTFT"} ${first}ms${total}`;
}

/**
 * The page the customer navigated to, as a link.
 *
 * The URL is whatever the widget reported from the customer's browser, so it is
 * untrusted input: only `http`/`https` become links, which keeps a crafted
 * `javascript:` value from turning into a clickable script. Anything else
 * renders as plain text. `noopener noreferrer` so the opened page can't reach
 * back into the console via `window.opener`.
 *
 * `wrap-anywhere` (ENG-2939): a URL carrying a long query string is a single
 * unbreakable token, and a browser lets one of those spill out of its box
 * rather than clip it, so the text escaped the row's grey pill entirely.
 * Breaking it lets the URL stay whole and wrap inside the 408px label cap
 * `InvisibleRow` already sets. `break-all` would also break the token, but it
 * ignores the natural break points after `/` and `?` that keep a wrapped URL
 * readable.
 */
function linkifyUrl(content: string): React.ReactNode {
  let parsed: URL;
  try {
    parsed = new URL(content.trim());
  } catch {
    return content;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return content;
  return (
    <a href={parsed.href} target="_blank" rel="noopener noreferrer" className="cursor-pointer wrap-anywhere text-grey-900 underline">
      {content}
    </a>
  );
}

/**
 * Icon for an `escalation_event` row, and for any other invisible row that has
 * no dedicated branch above and falls through to the same renderer. The branch
 * glyph is the default because most of these rows really are routing (offers,
 * rerouting, exhaustion), but a few say something else and shouldn't borrow
 * routing's symbol.
 */
function invisibleRowIcon(msgType: string, isTicketRow: boolean, subType: unknown): React.ReactNode {
  if (isTicketRow) return <IoTicketOutline size={16} />;
  // Closing is archive-like: the conversation is filed away, and reopens in
  // place if the customer writes again.
  if (subType === "conversation_closed") return <IoArchiveOutline size={16} />;
  // A takeover is a handoff between two people, not the system picking someone.
  if (subType === "takeover_pulled") return <IoSwapHorizontalOutline size={16} />;
  // The offer was taken back before anyone answered — nothing was routed.
  if (subType === "offer_withdrawn") return <IoRemoveCircleOutline size={16} />;
  // The agent finished and handed back to the bot: done, not filed away.
  if (subType === "escalation_completed" || subType === "assignment_auto_completed") return <IoCheckmarkDoneOutline size={16} />;
  // Bridging joins two phone legs — the only escalation row that is literally
  // about a live call.
  if (subType === "bridge_triggered") return <IoCallOutline size={16} />;
  // Routing ran out of people. The one row here that reports a failure, so it
  // gets the only alert glyph in the set.
  if (subType === "routing_exhausted") return <IoAlertCircleOutline size={16} />;
  // Landed on a team, so the glyph is people rather than a path.
  if (subType === "routing_resolved") return <IoPeopleOutline size={16} />;
  // Going round again after the first attempt didn't stick.
  if (subType === "reroute_started") return <IoRepeatOutline size={16} />;
  // Nobody answered in time — the clock is the whole story.
  if (subType === "assignment_timed_out") return <IoTimerOutline size={16} />;
  // A named person actively declined, as opposed to the clock running out.
  if (subType === "assignment_rejected") return <IoCloseCircleOutline size={16} />;
  // Its mirror: same shape, opposite mark, so the pair reads at a glance.
  if (subType === "assignment_accepted") return <IoCheckmarkCircleOutline size={16} />;
  // The offer itself: something held out to an agent, not yet answered.
  if (subType === "assignment_offered") return <IoHandRightOutline size={16} />;
  // ENG-3216 decision 66: the sequence that started this conversation.
  if (subType === "sequence_started") return <IoSendOutline size={16} />;
  return <IoGitBranchOutline size={16} />;
}

/**
 * Ticket rows read "Ticket created for this conversation"; the number belongs
 * next to the word it qualifies, so it's spliced in as "Ticket #1284 created
 * …" rather than tacked on the end as a separate chip. Black and underlined so
 * it reads as the row's one link, against the grey of the surrounding text.
 *
 * Falls back to appending when the sentence doesn't open with "Ticket" — the
 * copy is server-side and may not always.
 */
function linkifyTicketNumber(content: string, ticketNumber: number, onTicketClick?: (ticketNumber: number) => void): React.ReactNode {
  const label = `Ticket #${ticketNumber}`;
  const linkClasses = "cursor-pointer text-grey-900 underline";
  const link = onTicketClick ? (
    <button type="button" onClick={() => onTicketClick(ticketNumber)} className={linkClasses}>
      {label}
    </button>
  ) : (
    <span className={linkClasses}>{label}</span>
  );

  const opensWithTicket = /^ticket\b/i.test(content);
  return opensWithTicket ? (
    <>
      {link}
      {content.slice("Ticket".length)}
    </>
  ) : (
    <>
      {content} {link}
    </>
  );
}

/** "Today, 2:00 PM" / "Friday, Jul 3, 2:00 PM" — the Figma date-divider label. */
function formatDateDivider(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  if (date.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  // The year only when it cannot be inferred. Without it a divider reading
  // "Aug 18" is ambiguous on any transcript spanning more than a year
  // (PROD-773).
  const datePart = date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {})
  });
  return `${datePart}, ${time}`;
}

/**
 * Shared conversation timeline — renders all message/event types in sequence.
 * Used by AI Conversations, Contact Center V3, and Testing views.
 *
 * Uses the chat-ui MessageBubble for regular messages, plus specialized cards
 * for system prompts, LLM calls, tool calls, and other event types.
 */
export function ConversationTimeline({
  messages,
  isTyping = false,
  emptyMessage = "No messages in this conversation",
  showDebug = false,
  debugLevel = "none",
  onRequestLLMRecord,
  llmRecords,
  channel,
  userName,
  agentName,
  ownRole = "user",
  palette = "widget",
  newMessagesAfterIndex,
  onAgentClick,
  onTicketClick,
  header
}: ConversationTimelineProps): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);

  // The viewer's effective debug level for internal-row gating. `debugLevel` is
  // honoured as given, "none" included.
  //
  // It used to be promoted to "agent", on the assumption that "none" only ever
  // meant "caller did not specify" and never a viewer's own choice. Contact
  // Center v3 broke that assumption: "none" is the real key behind its "Clean"
  // option, so picking Clean silently rendered at agent level. A page load hid
  // that (the REST fetch filters server-side using the selected level, so the
  // rows never arrive), but live socket rows skip that filter and reached this
  // clamp instead, which is why internal content flashed into Clean only on
  // live bot actions (PROD-771). Callers that want the agent tier now say so.
  //
  // Defense-in-depth beside the server filters: live socket rows arrive
  // pre-routed by tier room, but a message must also never RENDER above the
  // level the viewer has actually selected.
  const viewerLevel: DebugLevel = showDebug ? "admin" : debugLevel;

  // Voice transcript "elapsed" labels anchor on the first real conversational message —
  // not messages[0], which is typically the system_prompt event saved at conversation setup.
  // Falls back to messages[0] if no user/assistant messages have arrived yet.
  const conversationStartTime = (messages.find((m) => m.role === "user" || m.role === "assistant") ?? messages[0])?.createdAt;

  // Close-time analytics pills render at the message they cite (ENG-3195).
  const orderedMessages = useMemo(() => anchorAnalyticsEvents(messages), [messages]);

  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
      {header}
      {messages.length === 0 && !isTyping ? (
        <p className="text-center text-sm text-grey-400">{emptyMessage}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {(() => {
            // Divider trackers — recomputed per render, consumed in order below.
            let lastRenderedDay = "";
            let newMarkerPlaced = newMessagesAfterIndex == null;

            return orderedMessages.map((msg) => {
              const msgType = msg.type || "message";

              // Date + "New Messages" dividers precede plain conversational
              // messages (never debug/event cards).
              let dividers: React.ReactNode = null;
              if (msgType === "message" && msg.createdAt) {
                const day = new Date(msg.createdAt).toDateString();
                const showDate = day !== lastRenderedDay;
                lastRenderedDay = day;
                const showNewMarker = !newMarkerPlaced && msg.messageIndex > (newMessagesAfterIndex as number);
                if (showNewMarker) newMarkerPlaced = true;
                if (showDate || showNewMarker) {
                  dividers = (
                    <>
                      {showDate && <p className="body-mono py-2 text-center text-grey-600">{formatDateDivider(msg.createdAt)}</p>}
                      {showNewMarker && (
                        <div className="flex items-center gap-2 py-2">
                          <div className="h-px flex-1 bg-orange-300" />
                          <span className="body-mono-alt tracking-wider text-orange-500 uppercase">New Messages</span>
                          <div className="h-px flex-1 bg-orange-300" />
                        </div>
                      )}
                    </>
                  );
                }
              }

              // Every internal row below is the shared Figma "Chat Invisibles"
              // pill (see InvisibleRow). Only the takeover notice differs — it
              // keeps the teal fill from node 72:7397.

              // System prompt — debug only
              if (msgType === "system_prompt") {
                if (!showDebug) return null;
                return <SystemPromptCard key={msg.messageId} content={msg.content} metadata={msg.metadata} createdAt={msg.createdAt} />;
              }

              if (msgType === "session_resume") {
                return <InvisibleRow key={msg.messageId} icon={<IoRefreshOutline size={16} />} label={msg.content} createdAt={msg.createdAt} />;
              }

              if (msgType === "page_navigation") {
                // Agent-tier row (reclassified from admin, ENG-2957) — default grey pill.
                return (
                  <InvisibleRow
                    key={msg.messageId}
                    icon={<IoNavigateOutline size={16} />}
                    label={linkifyUrl(msg.content)}
                    createdAt={msg.createdAt}
                  />
                );
              }

              // Tool call — admin-only expandable card. Agents work for the
              // operator, not Swivl: bot machinery is never their business
              // (Chuck, 2026-08-18). The server filter + socket rooms already
              // withhold these below admin; this gate is defense-in-depth.
              if (msgType === "tool_call") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                return (
                  <ToolCallCard
                    key={msg.messageId}
                    content={msg.content}
                    metadata={msg.metadata}
                    createdAt={msg.createdAt}
                    debugLevel={viewerLevel}
                  />
                );
              }

              // LLM call — debug only, expandable card
              if (msgType === "llm_call") {
                if (!showDebug) return null;
                const llmCallId = msg.botLLMCallId;
                return (
                  <LLMCallCard
                    key={msg.messageId}
                    content={msg.content}
                    metadata={msg.metadata}
                    createdAt={msg.createdAt}
                    fullRecord={llmCallId && llmRecords ? llmRecords.get(llmCallId) : undefined}
                    onRequestFullRecord={llmCallId && onRequestLLMRecord ? () => onRequestLLMRecord(llmCallId) : undefined}
                  />
                );
              }

              // Greeting/sequence triggers — admin-only bot machinery (Chuck, 2026-08-18).
              if (msgType === "greeting_trigger") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                return (
                  <InvisibleRow
                    key={msg.messageId}
                    icon={<IoSparklesOutline size={16} />}
                    label="Greeting trigger"
                    createdAt={msg.createdAt}
                    tone="purple"
                  >
                    <p className="body-sm whitespace-pre-wrap text-grey-600">{msg.content}</p>
                  </InvisibleRow>
                );
              }

              if (msgType === "sequence_trigger") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                const meta = (msg.metadata ?? {}) as any;
                const label = ["Sequence trigger", meta.sequenceSlug, meta.channel].filter(Boolean).join(" · ");
                return (
                  <InvisibleRow key={msg.messageId} icon={<IoSendOutline size={16} />} label={label} createdAt={msg.createdAt} tone="purple">
                    <p className="body-sm whitespace-pre-wrap text-grey-600">{msg.content}</p>
                  </InvisibleRow>
                );
              }

              // Conversation Starter priming record — admin-only machinery, like the
              // greeting/sequence triggers (ENG-3110).
              if (msgType === "starter_trigger") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                const meta = (msg.metadata ?? {}) as { starterSlug?: unknown; mode?: unknown; channel?: unknown };
                const label = ["Conversation Starter", meta.starterSlug, meta.mode, meta.channel]
                  .filter((v) => typeof v === "string" && v)
                  .join(" · ");
                return (
                  <InvisibleRow key={msg.messageId} icon={<IoRocketOutline size={16} />} label={label} createdAt={msg.createdAt} tone="purple">
                    <p className="body-sm whitespace-pre-wrap text-grey-600">{msg.content}</p>
                    {msg.metadata ? <HumanizedData data={msg.metadata} /> : undefined}
                  </InvisibleRow>
                );
              }

              // Widget-CTA priming record — admin-only machinery (ENG-3166).
              if (msgType === "cta_trigger") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                const meta = (msg.metadata ?? {}) as { ctaType?: unknown; ctaName?: unknown; actionId?: unknown };
                const label = ["CTA opening", meta.ctaName, meta.ctaType, meta.actionId].filter((v) => typeof v === "string" && v).join(" · ");
                return (
                  <InvisibleRow key={msg.messageId} icon={<IoRocketOutline size={16} />} label={label} createdAt={msg.createdAt} tone="purple">
                    <p className="body-sm whitespace-pre-wrap text-grey-600">{msg.content}</p>
                    {msg.metadata ? <HumanizedData data={msg.metadata} /> : undefined}
                  </InvisibleRow>
                );
              }

              // Problem marker — something went wrong and a fallback was taken. Agent
              // tier by design: agents, Swivl staff and admins all see it (Chuck,
              // 2026-08-23). Grey pill with a warning glyph; the detail expands.
              if (msgType === "problem") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                const meta = (msg.metadata ?? {}) as { source?: unknown };
                const label = ["Problem", typeof meta.source === "string" ? meta.source : undefined].filter(Boolean).join(" · ");
                return (
                  <InvisibleRow key={msg.messageId} icon={<IoWarningOutline size={16} />} label={label} createdAt={msg.createdAt}>
                    <p className="body-sm whitespace-pre-wrap text-grey-600">{msg.content}</p>
                    {msg.metadata ? <HumanizedData data={msg.metadata} /> : undefined}
                  </InvisibleRow>
                );
              }

              // Analytics event — debug only
              if (msgType === "analytics_event") {
                if (!showDebug) return null;
                const meta = (msg.metadata ?? {}) as any;
                const label = ["Analytics", meta.type || "event", meta.value].filter(Boolean).join(" · ");
                // Close-time events (ENG-3195) carry their evidence — cited
                // message, source — beyond type/value; show it behind the
                // expand. Runtime tool events have nothing extra and render
                // as a plain row exactly as before.
                const { type: _type, value: _value, ...extra } = meta;
                const hasExtra = Object.keys(extra).length > 0;
                // A close-time event renders at the cited message's moment,
                // not the close time — the pill sits inline at that message
                // (anchorAnalyticsEvents), so its clock must agree with its
                // neighbours. The audit truth (recorded at close) stays on
                // the event row and behind the expand (`source`).
                const displayedAt = typeof meta.messageAt === "string" ? meta.messageAt : msg.createdAt;
                return (
                  <InvisibleRow key={msg.messageId} icon={<IoStatsChartOutline size={16} />} label={label} createdAt={displayedAt} tone="purple">
                    {hasExtra ? <HumanizedData data={extra} /> : undefined}
                  </InvisibleRow>
                );
              }

              if (msgType === "sandbox_action") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                const meta = (msg.metadata ?? {}) as any;
                return (
                  <InvisibleRow
                    key={msg.messageId}
                    icon={<IoFlaskOutline size={16} />}
                    label={`Sandbox · ${meta.toolName ?? "action"}`}
                    createdAt={msg.createdAt}
                    tone="purple"
                  >
                    {meta.wouldHaveDone ? <HumanizedData data={meta.wouldHaveDone} /> : undefined}
                  </InvisibleRow>
                );
              }

              // A data fetch behind a picker (filter refresh, zip search) —
              // admin-only bot machinery, with the params/results it carries
              // behind the expand.
              if (msgType === "picker_api_call") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                return (
                  <InvisibleRow key={msg.messageId} icon={<IoSearchOutline size={16} />} label={msg.content} createdAt={msg.createdAt} tone="purple">
                    {msg.metadata ? <HumanizedData data={msg.metadata} /> : undefined}
                  </InvisibleRow>
                );
              }

              // A coaching nudge the agent acted on (e.g. texted the caller
              // cross-channel). A send, not routing — so a chat glyph, and the
              // metadata (who sent it, the SMS conversation) behind the expand.
              if (msgType === "nudge_action") {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                return (
                  <InvisibleRow key={msg.messageId} icon={<IoChatbubbleEllipsesOutline size={16} />} label={msg.content} createdAt={msg.createdAt}>
                    {msg.metadata ? <HumanizedData data={msg.metadata} /> : undefined}
                  </InvisibleRow>
                );
              }

              // System event. Takeover notices get their own teal treatment
              // (Figma node 72:7397); everything else — e.g. the hidden
              // hand-back instruction written when an agent returns control to
              // the bot — is an internal row like any other.
              if (msgType === "event") {
                if (isTakeoverEventMessage(msg)) {
                  return <TakeoverEventNotice key={msg.messageId} message={msg} onAgentClick={onAgentClick} variant="console" />;
                }
                // Shown in full rather than behind an expand: it's one sentence,
                // and hiding it costs more than the space it takes.
                return <InvisibleRow key={msg.messageId} icon={<IoTerminalOutline size={16} />} label={msg.content} createdAt={msg.createdAt} />;
              }

              // Internal rows the user NEVER saw (escalation telemetry and any
              // other invisible type without a dedicated card above) — a clear
              // agent-only treatment, never a chat bubble (ENG-2946): dashed
              // purple row with an AGENT ONLY badge. Falling through to the
              // bubble path made telemetry look like bot messages.
              // Rows above the viewer's level (runtime diagnostics are
              // admin-only per the shared classification) render nothing.
              if (msgType === "escalation_event" || msg.visible === false) {
                if (!debugLevelAtLeast(viewerLevel, messageMinDebugLevel(msg))) return null;
                // ENG-3005: ticket rows carry the ticket's number — render a
                // chip that opens it when the host view wired the navigation.
                const eventTicketNumber = (msg.metadata as { ticketNumber?: unknown } | undefined)?.ticketNumber;
                const isTicketRow = typeof eventTicketNumber === "number";
                const eventSubType = (msg.metadata as { subType?: unknown } | undefined)?.subType;
                // Decision 66: the "started by" line learns its delivery outcome once known.
                const outcomeLabel = (msg.metadata as { outcomeLabel?: unknown } | undefined)?.outcomeLabel;
                // ENG-3821: the end-of-escalation rows read as a plain phrase for agents, derived
                // here so rows written before the change read the same; admins see the stored text.
                const agentFacing =
                  viewerLevel === "admin" ? null : agentFacingEscalationLabel(eventSubType, msg.metadata as Record<string, unknown> | undefined);
                const eventContent =
                  agentFacing ??
                  (eventSubType === "sequence_started" && typeof outcomeLabel === "string" ? `${msg.content} · ${outcomeLabel}` : msg.content);
                // No "AGENT ONLY" badge (Chuck, 2026-08-08): the pill treatment
                // IS the internal-row signal; a badge on every row was noise.
                return (
                  <InvisibleRow
                    key={msg.messageId}
                    icon={invisibleRowIcon(msgType, isTicketRow, eventSubType)}
                    label={
                      isTicketRow
                        ? linkifyTicketNumber(msg.content, eventTicketNumber, onTicketClick)
                        : linkifyAgentNames(eventContent, agentRefsFromMetadata(msg.metadata as Record<string, unknown> | undefined), onAgentClick)
                    }
                    createdAt={msg.createdAt}
                    // Ticket rows share the teal fill with `TakeoverEventNotice`:
                    // both are rows where the conversation reached a person, as
                    // opposed to the machine steps around them.
                    // Teal for the rows that reached a person; purple for the
                    // ones only an admin can see, so the tier is visible without
                    // switching the filter to find out.
                    tone={isTicketRow ? "teal" : messageMinDebugLevel(msg) === "admin" ? "purple" : "grey"}
                  >
                    {/* The label is written for agents (no ids, no Twilio internals — ENG-3821); the
                        admin view keeps every detail in the expandable metadata block. */}
                    {viewerLevel === "admin" && msg.metadata ? <HumanizedData data={msg.metadata} /> : undefined}
                  </InvisibleRow>
                );
              }

              const turnMeta = msg.metadata as { ttfaMs?: number; turnFirstTokenLatencyMs?: number; turnLatencyMs?: number } | undefined;
              const isAssistant = msg.role === "assistant";

              // Voice reads as the same thread as web and SMS (one style across
              // channels); only its footer differs — it carries the time into
              // the call alongside the wall clock.
              const isVoice = channel === "voice";
              const voiceTimeLabel = isVoice ? formatVoiceTime(conversationStartTime, msg.createdAt) : undefined;

              // Web/SMS — use the shared MessageBubble or StructuredMessage.
              // Structured-message protocol (2026-04-28) per §3.6: the agent
              // timeline renders the same components as the user widget,
              // in read-only mode. Selected state, link-click annotations,
              // and dismissals all surface here for agent visibility.
              const contentType = msg.contentType;
              const payload = msg.payload;
              const persistedActionTaken = msg.actionTaken;
              const onSelect = msg.onSelect ?? (payload && (payload as any).onSelect);

              // `msg` is already the canonical render shape — pass it straight to
              // the shared renderers, only collapsing `system` → `assistant` for
              // the bubble and applying the legacy payload-derived `onSelect`.
              const chatMsg: ChatMessage = {
                ...msg,
                role: msg.role === "user" ? "user" : "assistant",
                ...(onSelect ? { onSelect } : {})
              };

              if (contentType === "structured") {
                return (
                  <div key={msg.messageId}>
                    {dividers}
                    <StructuredMessage message={chatMsg} readOnly debugLevel={debugLevel} ownRole={ownRole} />
                  </div>
                );
              }

              // Text message — bubble, then a pill per recorded link click.
              const linkClicks: LinkClick[] = Array.isArray(persistedActionTaken) ? (persistedActionTaken as LinkClick[]) : [];
              return (
                <div key={msg.messageId}>
                  {dividers}
                  <MessageBubble
                    message={chatMsg}
                    ownRole={ownRole}
                    palette={palette}
                    timeLabel={voiceTimeLabel}
                    botLabel={isVoice ? agentName || "Agent" : undefined}
                  />
                  {isAssistant && latencyReadout(isVoice, turnMeta) && (
                    // Sits under the bubble it describes, so it follows the same
                    // side the bubble is on rather than a fixed left indent.
                    <div
                      className={filterClasses("body-mono -mt-1 mb-3 flex text-grey-600", ownRole === "assistant" ? "justify-end" : "justify-start")}
                    >
                      {latencyReadout(isVoice, turnMeta)}
                    </div>
                  )}
                  {/* Ungated on purpose: a tenant's click is not a machine internal. */}
                  {linkClicks.map((click, i) => (
                    <InvisibleRow
                      key={`${msg.messageId}-click-${i}`}
                      icon={<IoLinkOutline size={16} />}
                      label={`Link clicked · ${click.source}`}
                      createdAt={click.clickedAt}
                      tone="teal"
                    >
                      {linkifyUrl(click.url)}
                    </InvisibleRow>
                  ))}
                </div>
              );
            });
          })()}
          {isTyping && <TypingIndicator />}
        </div>
      )}
    </div>
  );
}
